/**
 * XEP-0050 ad-hoc client for the user's own scheduled automations (cron jobs
 * and one-shots). Same arrangement as the per-stop rules: the frontend reads
 * the workflows node and asks the agent for every write.
 *
 * Ownership needs no field — the agent takes it from the IQ sender and answers
 * item-not-found for a job that is not yours.
 */
import { Client } from "@xmpp/client"
import i18n from "@/lib/i18n/i18n"
import { formField, sendCommand } from "./adhocForm"

export const UPDATE_NODE = "cron_update"
export const DELETE_NODE = "cron_delete"
export const PAUSE_NODE = "cron_pause"
export const RESUME_NODE = "cron_resume"

export type CronAutomationPatch = {
  name?: string
  /**
   * The agent-turn instruction. Omit for handler-payload workflows: the agent
   * rewrites the payload to an agent-turn whenever this is present, which
   * would turn a built-in job (a brief, a fuel monitor) into a plain prompt.
   */
  message?: string
  /** 5-field cron expression. Must travel together with `tz`. */
  schedule?: string
  /** IANA timezone. Must travel together with `schedule`. */
  tz?: string
  /** ISO-8601 instant for a one-shot. Mutually exclusive with schedule/tz. */
  runAt?: string
}

export const updateCronAutomation = async (
  xmpp: Client,
  id: string,
  patch: CronAutomationPatch,
): Promise<void> => {
  if (patch.runAt !== undefined && (patch.schedule ?? patch.tz) !== undefined) {
    // The agent rejects this too; failing here keeps the reason legible.
    throw new Error(i18n.t("ai.automationMutation.scheduleOrRunAt"))
  }

  await sendCommand(xmpp, UPDATE_NODE, [
    formField("id", id),
    formField("name", patch.name),
    formField("message", patch.message),
    formField("schedule", patch.schedule),
    formField("tz", patch.tz),
    formField("run_at", patch.runAt),
  ])
}

export const deleteCronAutomation = async (
  xmpp: Client,
  id: string,
): Promise<void> => {
  await sendCommand(xmpp, DELETE_NODE, [formField("id", id)])
}

export const setCronAutomationStatus = async (
  xmpp: Client,
  id: string,
  status: "active" | "paused",
): Promise<void> => {
  await sendCommand(xmpp, status === "paused" ? PAUSE_NODE : RESUME_NODE, [
    formField("id", id),
  ])
}
